<template>
  <div class="gm-page tabletop-map-player">
    <!-- Top Toolbar -->
    <div class="gm-topbar">
      <div class="toolbar-section">
        <div>
          <div class="gm-eyebrow">Game Master</div>
          <h1 class="gm-title">Tabletop Map Player</h1>
        </div>
        <span v-if="currentMap?.metadata?.name" class="map-name">{{ currentMap.metadata.name }}</span>
      </div>

      <div class="gm-topbar__actions">
        <button class="gm-btn gm-btn--ghost" @click="backToEditor">
          Back to Editor
        </button>
        <button class="gm-btn gm-btn--primary" @click="showCharacterSpawner = true" :disabled="!currentMap">
          Spawn Character
        </button>
        <button class="gm-btn" @click="importMap">
          Import Map
        </button>
        <button class="gm-btn gm-btn--danger" @click="clearAll" :disabled="!currentMap">
          Clear All
        </button>
      </div>
    </div>

    <!-- Character Spawner Modal -->
    <div v-if="showCharacterSpawner" class="modal-overlay" @click.self="showCharacterSpawner = false">
      <CharacterSpawner
        @close="showCharacterSpawner = false"
        @spawn="handleSpawnCharacter"
      />
    </div>

    <!-- Main Content Area -->
    <div class="gm-page__body player-content">
      <!-- Center Panel - Canvas -->
      <div class="center-panel gm-panel gm-panel--flush">
        <div v-if="loading" class="gm-empty">
          <span class="dx-loader" aria-hidden="true"></span>
          <h2 class="gm-empty__title">Loading Map...</h2>
          <p class="gm-empty__hint">Fetching template from backend</p>
        </div>
        <div v-else-if="!currentMap" class="gm-empty">
          <h2 class="gm-empty__title">No Map Loaded</h2>
          <p class="gm-empty__hint">Import a map from the Tabletop Map Editor to start testing</p>
          <button class="gm-btn gm-btn--primary" @click="importMap">
            Import Map
          </button>
        </div>
        <PlayerCanvas v-else :key="currentMap?.metadata?.created || 'default'" />
      </div>

      <!-- Right Panel - Controls -->
      <div class="right-panel gm-scroll">
        <PlayerControlPanel />
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      ref="importInput"
      type="file"
      accept=".png,.json,image/png,application/json"
      style="display: none"
      @change="handleImportFile"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PlayerCanvas from '@/components/TabletopPlayer/PlayerCanvas.vue';
import PlayerControlPanel from '@/components/TabletopPlayer/PlayerControlPanel.vue';
import CharacterSpawner from '@/components/TabletopPlayer/CharacterSpawner.vue';
import { extractMetadataFromPNG } from '@/utils/pngMetadata.js';
import battleMapService from '@/services/battleMapService';
import { SkillsGameApi } from '@/api/backendService';

export default {
  name: 'TabletopMapPlayer',
  components: {
    PlayerCanvas,
    PlayerControlPanel,
    CharacterSpawner
  },
  data() {
    return {
      loading: false,
      showCharacterSpawner: false
    };
  },
  computed: {
    ...mapGetters('tabletopPlayer', [
      'currentMap',
      'players'
    ])
  },
  async mounted() {
    // Check if templateId is provided in route query params
    const templateId = this.$route.query.templateId;
    if (templateId) {
      console.log('[TabletopPlayer] Template ID from query:', templateId);
      await this.loadTemplateFromBackend(templateId);
    } else {
      console.log('[TabletopPlayer] No template ID provided in query params');
    }
  },
  methods: {
    ...mapActions('tabletopPlayer', [
      'loadMap',
      'setBackgroundImage',
      'spawnPlayer'
    ]),

    // Handle character spawn from CharacterSpawner
    async handleSpawnCharacter(character) {
      console.log('[TabletopPlayer] Spawning character:', character.name)
      console.log('[TabletopPlayer] Character data:', character)

      try {
        // Load character skills from GM API
        console.log('[TabletopPlayer] Loading skills for character:', character.id)
        const skillsResponse = await SkillsGameApi.skillsGmSkillsList(character.id)
        const skills = skillsResponse.data.results || skillsResponse.data || []
        console.log('[TabletopPlayer] ✅ Loaded', skills.length, 'skills')

        // Extract skill objects from LearnedSkill wrapper
        const skillObjects = skills.map(learnedSkill => learnedSkill.skill)

        // NOTE: There's no GM endpoint for character items in the backend
        // Items would need to be added to the character serializer or a new GM endpoint created
        // For now, items will be empty unless the character object includes them
        let items = []
        if (character.items && Array.isArray(character.items)) {
          items = character.items
          console.log('[TabletopPlayer] ✅ Using character.items:', items.length)
        } else {
          console.warn('[TabletopPlayer] ⚠️ No items available (no GM endpoint for character items)')
        }

        // Spawn character at center of map
        const gridConfig = this.currentMap?.grid
        const centerX = Math.floor((gridConfig?.columns || 10) / 2)
        const centerY = Math.floor((gridConfig?.rows || 10) / 2)

        this.spawnPlayer({
          x: centerX,
          y: centerY,
          layer: 0,
          name: character.name,
          image: character.biography?.avatar || character.avatar,
          description: `${character.rank?.name || 'Character'} - ${character.npc ? 'NPC' : 'Player'}`,
          actionPoints: character.action_points || 10,
          skills: skillObjects,
          items: items,
          type: character.npc ? 'npc' : 'player',
          alive: character.alive !== undefined ? character.alive : true, // Use backend alive property
          // Store full character data for later use
          characterData: {
            ...character,
            skills: skillObjects,
            items: items
          }
        })

        console.log('[TabletopPlayer] ✅ Character spawned at', centerX, centerY)
        console.log('[TabletopPlayer] Skills:', skillObjects.length, 'Items:', items.length)

        // Close spawner
        this.showCharacterSpawner = false
      } catch (error) {
        console.error('[TabletopPlayer] ❌ Error spawning character:', error)
        console.error('[TabletopPlayer] Error details:', error.message)
      }
    },

    // Load template from backend
    async loadTemplateFromBackend(templateId) {
      this.loading = true;
      try {
        console.log('[TabletopPlayer] Loading template from backend:', templateId);
        const mapData = await battleMapService.loadTemplate(templateId);

        // Load the map data
        this.loadMap(mapData);

        // Set background image if present
        if (mapData.backgroundImage) {
          this.setBackgroundImage(mapData.backgroundImage);
        }

        console.log('[TabletopPlayer] ✅ Template loaded successfully');
      } catch (error) {
        console.error('[TabletopPlayer] ❌ Error loading template:', error.message);
      } finally {
        this.loading = false;
      }
    },

    // Navigate back to editor
    backToEditor() {
      this.$router.push({ name: 'TabletopMapEditor' });
    },

    importMap() {
      this.$refs.importInput.click();
    },

    async handleImportFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      console.log('[Player Import] Starting import process for file:', file.name, 'Type:', file.type);

      try {
        // Check if it's a PNG file
        if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
          console.log('[Player Import] Detected PNG file, extracting metadata...');

          // Extract metadata from PNG
          const { mapData, imageDataURL, hasMetadata } = await extractMetadataFromPNG(file);

          if (hasMetadata && mapData) {
            // PNG has embedded map data - load it
            console.log('[Player Import] ✅ PNG has metadata - loading map data');
            this.loadMap(mapData);

            // Set the background image from the PNG
            console.log('[Player Import] Setting background image...');
            this.setBackgroundImage(imageDataURL);

            console.log('[Player Import] ✅ Map import completed successfully!');
          } else {
            // PNG has no metadata
            console.warn('[Player Import] PNG has no embedded map data - cannot load');
          }
        } else if (file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')) {
          // Legacy JSON import support
          console.log('[Player Import] Detected JSON file, loading...');
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const mapData = JSON.parse(e.target.result);
              console.log('[Player Import] JSON parsed, loading map...');
              this.loadMap(mapData);
              console.log('[Player Import] ✅ JSON map imported successfully!');
            } catch (error) {
              console.error('[Player Import] ❌ Error parsing JSON:', error.message);
            }
          };
          reader.readAsText(file);
        } else {
          console.warn('[Player Import] Invalid file type - please select PNG or JSON');
        }
      } catch (error) {
        console.error('[Player Import] ❌ Error importing map:', error.message);
      }

      // Reset file input
      event.target.value = '';
    },

    clearAll() {
      console.log('[TabletopPlayer] Clearing map and all players');
      this.loadMap(null);
    }
  }
};
</script>

<style scoped>
.toolbar-section {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-lg);
}

.map-name {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
  font-style: italic;
}

/* Main Content */
.player-content {
  gap: var(--gm-gap);
  padding: var(--gm-gap);
  overflow: hidden;
}

.center-panel {
  flex: 1;
  position: relative;
}

.right-panel {
  width: 20rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
  padding-bottom: var(--gm-dock-clearance);
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .right-panel {
    width: 17.5rem;
  }
}

@media (max-width: 768px) {
  .player-content {
    flex-direction: column;
  }

  .right-panel {
    width: 100%;
    max-height: 18.75rem;
  }

  .center-panel {
    min-height: 25rem;
  }
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
}
</style>
